import {
  LotteryBatchStateGroup,
  LotteryBatchStepStatus,
} from 'src/types/Lottery';

export interface LotteryChipColour {
  background: string;
  color: string;
}

export const FALLBACK_CHIP_COLOUR: LotteryChipColour = {
  background: 'grey.200',
  color: 'text.secondary',
};

export const DISPOSITION_CHIP_COLOURS: Record<string, LotteryChipColour> = {
  request_for_documents: {
    background: 'success.lighter',
    color: 'success.dark',
  },
  rejected: { background: 'error.lighter', color: 'error.dark' },
  ineligible: { background: 'error.lighter', color: 'error.dark' },
  set_as_pending: { background: 'grey.200', color: 'text.secondary' },
  not_interested: { background: 'grey.200', color: 'text.secondary' },
  skipped: { background: 'grey.200', color: 'text.secondary' },
  in_batch: { background: 'grey.200', color: 'text.secondary' },
  unknown: { background: 'grey.200', color: 'text.secondary' },
};

export const LOAD_OUTCOME_CHIP_COLOURS: Record<string, LotteryChipColour> = {
  invite_sent: { background: 'success.lighter', color: 'success.dark' },
  certification_created: { background: 'info.lighter', color: 'info.dark' },
  appended_to_existing: {
    background: 'secondary.lighter',
    color: 'secondary.dark',
  },
  failed: { background: 'warning.lighter', color: 'warning.dark' },
  not_loaded: { background: 'grey.200', color: 'text.secondary' },
  skipped_cert_exists: { background: 'grey.200', color: 'text.secondary' },
  creating: { background: 'warning.lighter', color: 'warning.dark' },
};

export const BATCH_STATE_GROUP_CHIP_COLOURS: Record<
  LotteryBatchStateGroup,
  LotteryChipColour
> = {
  in_progress: { background: 'info.lighter', color: 'info.dark' },
  completed: { background: 'success.lighter', color: 'success.dark' },
  partially_completed: { background: 'warning.lighter', color: 'warning.dark' },
  failed: { background: 'error.lighter', color: 'error.dark' },
};

export const DEGRADED_CHIP_COLOUR: LotteryChipColour = {
  background: 'warning.lighter',
  color: 'warning.dark',
};

// External-services health verdict → the same soft chip palette the status columns use.
export const HEALTH_STATUS_CHIP_COLOURS: Record<
  'operational' | 'degraded' | 'unavailable' | 'unknown',
  LotteryChipColour
> = {
  operational: { background: 'success.lighter', color: 'success.dark' },
  degraded: { background: 'warning.lighter', color: 'warning.dark' },
  unavailable: { background: 'error.lighter', color: 'error.dark' },
  unknown: FALLBACK_CHIP_COLOUR,
};

export const STEP_STATUS_CHIP_COLOURS: Record<
  LotteryBatchStepStatus,
  LotteryChipColour
> = {
  PENDING: { background: 'grey.200', color: 'text.secondary' },
  RUNNING: { background: 'info.lighter', color: 'info.dark' },
  COMPLETED: { background: 'success.lighter', color: 'success.dark' },
  FAILED: { background: 'error.lighter', color: 'error.dark' },
  SKIPPED: { background: 'grey.200', color: 'text.secondary' },
};

export type LotteryStatsLegendKey =
  | 'duplicates'
  | 'rejected'
  | 'ineligible'
  | 'set_as_pending'
  | 'creating'
  | 'created'
  | 'invites_sent'
  | 'failed';

// One palette colour per donut segment. Deliberately distinct — the mockup
// reused red for Ineligible and Failed and the old UI reused red three times and
// green twice, which makes adjacent slices ambiguous. The legend swatch and the
// slice both read this map (through the legend rows), so they cannot drift.
// `primary` is excluded because it is user-configurable and could collide with
// an `info`/`secondary` segment.
export const LOTTERY_STATS_SEGMENT_COLOURS: Record<
  LotteryStatsLegendKey,
  string
> = {
  duplicates: 'warning.main',
  rejected: 'signature.main',
  ineligible: 'error.main',
  failed: 'error.dark',
  set_as_pending: 'grey.main',
  creating: 'info.main',
  created: 'success.main',
  invites_sent: 'secondary.main',
};
